import {useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {areIntervalsOverlapping, getOverlappingDaysInIntervals, parse} from 'date-fns';
import {Observable, defer, of, concatMap, map, finalize, Subscription} from 'rxjs';
import {
  EmployeesApiService,
  useService,
  Workspace,
  WorkspaceBookType,
  WorkspacesApiService,
  WorkspaceStatuses,
} from '@innowise-group/core';
import {Button, Divider, Form, withForm, WithFormProps} from '@innowise-group/ui-kit';
import {ExcludeDates} from '@shared-components';
import {Dispatch, SetStateAction, useCallback, useEffect, useRef, useState} from 'react';
import {SelectStatusFormValues, WorkspaceStatusForm} from '../workspace-status-form';
import {WorkspaceNumberFormValues} from '../workspace-number-form';
import {
  WorkspaceEmployeeForm,
  WorkspaceEmployeeFormValues,
  WorkspaceEmployeeFormFields,
} from '../workspace-employee-form';
import {
  WorkspaceBookingDateForm,
  WorkspaceBookingDateFormFields,
  WorkspaceBookingDateFormValues,
} from '../workspace-booking-date-form';
import {WorkspaceCommentForm, WorkspaceCommentFormValues, WorkspaceCommentFormFields} from '../workspace-comment-form';
import {SelectStatusFormFields} from '../workspace-status-form';
import * as Styled from './add-workspace-form-styles';

type AddWorkspaceRoomFormValue = {
  room: {
    id: string;
    number: string;
  };
};

export type AddWorkspaceValues = WorkspaceNumberFormValues &
  SelectStatusFormValues &
  WorkspaceEmployeeFormValues &
  WorkspaceBookingDateFormValues &
  WorkspaceCommentFormValues &
  AddWorkspaceRoomFormValue;

type AddWorkspaceFormProps = {
  isVirtual: boolean;
  datesToExclude?: ExcludeDates;
  officeId: string;
  workspaceId?: string | number;
  replaceEmployeeModal: (
    title?: string,
    text?: string,
    confirmationText?: string,
    cancelText?: string,
  ) => Observable<void>;
  warningModal?: (text: string, title: string, button: string) => void;
  revertCallback?: Dispatch<SetStateAction<Workspace[]>>;
};

const AddWorkspaceForm: React.FC<WithFormProps<AddWorkspaceValues> & AddWorkspaceFormProps> = ({
  onSubmit,
  onCancel,
  initialValues = {},
  isVirtual = false,
  datesToExclude,
  officeId,
  replaceEmployeeModal,
  warningModal,
  revertCallback,
}) => {
  const {
    handleSubmit,
    formState: {errors, isValid},
    watch,
    resetField,
  } = useFormContext();

  const [isEditing, setIsEditing] = useState(false);

  const subscriptionRef = useRef<Subscription>(new Subscription());
  const isMountedRef = useRef(true);

  const employeesApi = useService(EmployeesApiService);
  const workspacesApi = useService(WorkspacesApiService);
  const {t} = useTranslation();
  const {
    employee: prevEmployee,
    comment,
    dateRange: [startDay, endDay],
    status: prevStatus,
    id,
  } = initialValues;

  useEffect(() => {
    const isFreeStatus = (status?: WorkspaceBookType) =>
      status === WorkspaceStatuses.Free || status === WorkspaceStatuses.CoworkingFree;

    const isBookedStatus = (status?: WorkspaceBookType) =>
      status === WorkspaceStatuses.Booked || status === WorkspaceStatuses.CoworkingBooked;

    const subscription = watch((values: Partial<AddWorkspaceValues>, {name, type}) => {
      if (name !== SelectStatusFormFields.WorkspaceStatus || type !== 'change') {
        return;
      }

      const newStatus = values?.status;

      if (isFreeStatus(newStatus) && values?.employee) {
        resetField(WorkspaceEmployeeFormFields.Employee, {defaultValue: null});
      }

      if (!isBookedStatus(newStatus) && values?.dateRange?.[0]) {
        resetField(WorkspaceBookingDateFormFields.DateRange, {defaultValue: [null, null]});
      }

      if (newStatus !== WorkspaceStatuses.Reserved && values?.comment) {
        resetField(WorkspaceCommentFormFields.Comment, {defaultValue: null});
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, resetField]);

  useEffect(() => {
    isMountedRef.current = true;
    const subscription = subscriptionRef.current;
    return () => {
      isMountedRef.current = false;
      subscription.unsubscribe();
    };
  }, []);

  const confirmReplaceEmployee = useCallback(
    (
      targetEmployee?: string,
      status?: WorkspaceBookType,
      selectedDates?: {start: Date; end: Date} | null,
      areDatesOverlapped?: boolean,
    ): Observable<{mayRevert: boolean; workspaceToRevert: Workspace | null}> => {
      return defer(() => {
        return workspacesApi.getWorkspaceById(id).pipe(
          concatMap((previousWorkspace) => {
            const {
              status: prevStatus,
              employee: {id: prevEmployee, ...prevEmployeeData},
              activeRequests,
            } = previousWorkspace;

            // Common check: date overlap validation (applies to both cases)
            if (
              areDatesOverlapped &&
              (prevStatus === WorkspaceStatuses.Booked || prevStatus === WorkspaceStatuses.CoworkingBooked)
            ) {
              return replaceEmployeeModal(
                t('modals.changeWorkspaceStatusTitle'),
                t('modals.changeWorkspaceStatusConfirmationText'),
                t('buttons.proceed'),
                t('buttons.cancel'),
              ).pipe(map(() => ({mayRevert: false, workspaceToRevert: null})));
            }

            if (activeRequests?.length) {
              const hasPermanentRequest = activeRequests.some(({range}) => !range.startDay && !range.endDay);
              const hasDateConflict =
                selectedDates &&
                activeRequests.some(({range}) => {
                  if (!range.startDay || !range.endDay) return false;

                  const requestDates = {
                    start: parse(range.startDay, 'dd.MM.yyyy', new Date()),
                    end: parse(range.endDay, 'dd.MM.yyyy', new Date()),
                  };

                  return areIntervalsOverlapping(requestDates, selectedDates, {inclusive: true});
                });

              const hasTemporaryRequestWithoutSelectedDates =
                !selectedDates && activeRequests.some(({range}) => range.startDay && range.endDay);

              if (hasDateConflict || hasPermanentRequest || hasTemporaryRequestWithoutSelectedDates) {
                return replaceEmployeeModal(
                  t('modals.confirmTitle'),
                  t('modals.conflictWithActiveRequests'),
                  t('buttons.proceed'),
                  t('buttons.cancel'),
                ).pipe(map(() => ({mayRevert: false, workspaceToRevert: null})));
              }
            }

            // If no targetEmployee, return early after common validations
            if (!targetEmployee) {
              return of({mayRevert: false, workspaceToRevert: null});
            }

            // If targetEmployee exists, fetch employee data
            return employeesApi.getEmployeeById(targetEmployee).pipe(
              concatMap((employeeData) => {
                const permanentWorkspace = employeeData?.workspaces?.find(
                  (ws) => ws.status === WorkspaceStatuses.Occupied || ws.status === WorkspaceStatuses.Remote,
                );

                return permanentWorkspace
                  ? workspacesApi
                      .getWorkspaceById(permanentWorkspace.id)
                      .pipe(map((workspaceToRevert) => ({employeeData, workspaceToRevert, permanentWorkspace})))
                  : of({workspaceToRevert: null, employeeData, permanentWorkspace});
              }),
              concatMap(({employeeData, permanentWorkspace, workspaceToRevert}) => {
                const temporaryWorkspacesInOffice = employeeData.workspaces?.filter(
                  (ws) =>
                    (ws.status === WorkspaceStatuses.Booked || ws.status === WorkspaceStatuses.CoworkingBooked) &&
                    ws.officeId === officeId &&
                    ws.id !== initialValues.id,
                );

                const temporaryWorkspaceOnThisDate = selectedDates
                  ? temporaryWorkspacesInOffice.find((ws) => {
                      const existingBookingDates = {start: new Date(ws?.startDay), end: new Date(ws?.endDay)};
                      const bookingsOverlap =
                        (status === WorkspaceStatuses.Booked || status === WorkspaceStatuses.CoworkingBooked) &&
                        getOverlappingDaysInIntervals(selectedDates, existingBookingDates);
                      return bookingsOverlap ? ws : false;
                    })
                  : undefined;

                const hasPermanentWorkspace = permanentWorkspace?.officeId === officeId;

                const hasPermanentWorkspaceCondition =
                  hasPermanentWorkspace &&
                  (status === WorkspaceStatuses.Booked || status === WorkspaceStatuses.CoworkingBooked);

                const prevOccupiedCondition =
                  prevStatus === WorkspaceStatuses.Occupied &&
                  (status === WorkspaceStatuses.Reserved ||
                    status === WorkspaceStatuses.Free ||
                    status === WorkspaceStatuses.CoworkingFree);

                if (hasPermanentWorkspaceCondition || prevOccupiedCondition) {
                  return replaceEmployeeModal(
                    t('modals.replaceWorkspaceStatusTitle'),
                    t('modals.hasWorkspaceInThisOffice', {
                      name: prevEmployeeData?.fullName,
                      address: '',
                    }),
                    t('buttons.yes'),
                    t('buttons.no'),
                  ).pipe(map(() => ({mayRevert: false, workspaceToRevert: null})));
                }

                if (
                  prevEmployee &&
                  employeeData?.id &&
                  prevStatus === WorkspaceStatuses.Occupied &&
                  prevEmployee !== employeeData?.id
                ) {
                  return replaceEmployeeModal(
                    t('modals.replaceWorkspaceStatusTitle'),
                    t('modals.hasWorkspaceInThisOffice', {
                      name: prevEmployeeData?.fullName,
                      address: '',
                    }),
                    t('buttons.yes'),
                    t('buttons.no'),
                  ).pipe(map(() => ({mayRevert: false, workspaceToRevert: null})));
                }

                if (temporaryWorkspacesInOffice?.length && status === WorkspaceStatuses.Occupied) {
                  return replaceEmployeeModal(
                    t('modals.replaceWorkspaceStatusTitle'),
                    t('modals.changeCurrentWorkspaceStatusConfirmationText', {
                      name: employeeData?.fullName,
                      seatNumber: initialValues.number,
                      roomName: initialValues.room.number,
                      adress: `${temporaryWorkspacesInOffice[0]?.city}, ${temporaryWorkspacesInOffice[0]?.address}`,
                    }),
                    t('buttons.yes'),
                    t('buttons.no'),
                  ).pipe(map(() => ({mayRevert: false, workspaceToRevert: null})));
                }
                if (
                  temporaryWorkspaceOnThisDate &&
                  (status === WorkspaceStatuses.Booked || status === WorkspaceStatuses.CoworkingBooked)
                ) {
                  return replaceEmployeeModal(
                    t('modals.replaceWorkspaceStatusTitle'),
                    t('modals.hasTemporaryWorkspaceMessage', {
                      name: employeeData?.fullName,
                      status: t('statuses.booked_secondary'),
                      workspace: temporaryWorkspacesInOffice?.map(({number}) => number).join(', №'),
                      room: temporaryWorkspaceOnThisDate?.room,
                      floor: temporaryWorkspaceOnThisDate?.floor,
                    }),
                    t('buttons.yes'),
                    t('buttons.no'),
                  ).pipe(map(() => ({mayRevert: false, workspaceToRevert: null})));
                }

                if (
                  prevStatus !== WorkspaceStatuses.Free &&
                  prevStatus !== WorkspaceStatuses.CoworkingFree &&
                  (status === WorkspaceStatuses.Free || status === WorkspaceStatuses.CoworkingFree) &&
                  prevEmployee !== employeeData.id
                ) {
                  return replaceEmployeeModal(
                    t('modals.replaceWorkspaceTableStatusTitle'),
                    t('modals.replaceWorkspaceTableStatusText'),
                    t('buttons.yes'),
                    t('buttons.no'),
                  ).pipe(map(() => ({mayRevert: false, workspaceToRevert: null})));
                }
                if (permanentWorkspace && status === WorkspaceStatuses.Occupied && prevEmployee !== employeeData.id) {
                  return replaceEmployeeModal(
                    t('modals.replaceWorkspaceStatusTitle'),
                    t('modals.hasPermanentWorkspaceMessage', {
                      name: employeeData?.fullName,
                      workspace: permanentWorkspace?.number,
                      room: permanentWorkspace?.room,
                      floor: permanentWorkspace?.floor,
                      office: `${permanentWorkspace?.city}, ${permanentWorkspace?.address}`,
                    }),
                    t('buttons.yes'),
                    t('buttons.no'),
                  ).pipe(map(() => ({mayRevert: true, workspaceToRevert})));
                }
                return of({mayRevert: false, workspaceToRevert});
              }),
            );
          }),
        );
      });
    },
    [
      workspacesApi,
      id,
      replaceEmployeeModal,
      t,
      initialValues.number,
      initialValues.room.number,
      employeesApi,
      officeId,
      initialValues.id,
    ],
  );

  const onSubmitHandler = useCallback(
    ({employee, status, dateRange: [startDay, endDay], comment, ...rest}: AddWorkspaceValues) => {
      const targetEmployee = employee || prevEmployee;
      const areDatesOverlapped = datesToExclude.some(({range}) => {
        return areIntervalsOverlapping(
          {start: new Date(range.start), end: new Date(range.end)},
          {start: new Date(startDay), end: new Date(endDay)},
          {inclusive: true},
        );
      });

      if (status) {
        const values: AddWorkspaceValues = {
          employee,
          status,
          dateRange: [startDay, endDay],
          comment: status === WorkspaceStatuses.Free || status === WorkspaceStatuses.CoworkingFree ? null : comment,
          ...rest,
        };

        setIsEditing(true);
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = new Subscription();

        const selectedDates =
          startDay && endDay
            ? {
                start: new Date(startDay),
                end: new Date(endDay),
              }
            : null;

        const subscription = confirmReplaceEmployee(
          targetEmployee || undefined,
          status,
          selectedDates,
          areDatesOverlapped,
        )
          .pipe(
            finalize(() => {
              if (isMountedRef.current) {
                setIsEditing(false);
              }
            }),
          )
          .subscribe({
            next: ({mayRevert, workspaceToRevert}) => {
              onSubmit(values);
              if (mayRevert && workspaceToRevert) {
                revertCallback?.((values) => [...values, workspaceToRevert]);
              }
            },
            error: (error) => {
              console.error('Error processing workspace submission:', error);
              if (isMountedRef.current) {
                setIsEditing(false);
              }
            },
          });

        subscriptionRef.current.add(subscription);
      }
    },
    [prevEmployee, datesToExclude, onSubmit, confirmReplaceEmployee, revertCallback],
  );

  const isWorkspaceInFreeStatus =
    watch(SelectStatusFormFields.WorkspaceStatus, prevStatus) === WorkspaceStatuses.Free ||
    watch(SelectStatusFormFields.WorkspaceStatus, prevStatus) === WorkspaceStatuses.CoworkingFree;

  const isReservedMsgShown =
    watch(SelectStatusFormFields.WorkspaceStatus) === WorkspaceStatuses.Reserved &&
    !(watch(WorkspaceEmployeeFormFields.Employee) || watch(WorkspaceCommentFormFields.Comment));

  return (
    <Form.Container onSubmit={handleSubmit(onSubmitHandler)}>
      {isReservedMsgShown && (
        <Styled.FormError>{errors?.comment?.message || errors?.employee?.message}</Styled.FormError>
      )}
      <Form.Layout layoutType="grid">
        <WorkspaceStatusForm defaultValue={prevStatus} isVirtual={isVirtual} />
      </Form.Layout>

      <Divider />

      <Form.Layout layoutType="grid" inset="top">
        <WorkspaceEmployeeForm
          defaultValue={prevEmployee}
          status={watch(SelectStatusFormFields.WorkspaceStatus, prevStatus)}
          comment={watch(WorkspaceCommentFormFields.Comment, comment)}
          isDisabled={isWorkspaceInFreeStatus}
        />
      </Form.Layout>

      <Divider />

      <Form.Layout layoutType="vertical" inset="top">
        <WorkspaceBookingDateForm
          defaultValue={{startDay, endDay}}
          status={watch(SelectStatusFormFields.WorkspaceStatus, prevStatus)}
          // datesToExclude={datesToExclude}
        />
      </Form.Layout>

      <Divider />

      {!isWorkspaceInFreeStatus && (
        <Form.Layout layoutType="vertical" inset="top">
          <WorkspaceCommentForm
            defaultValue={comment}
            status={watch(SelectStatusFormFields.WorkspaceStatus, prevStatus)}
            employee={watch(WorkspaceEmployeeFormFields.Employee, prevEmployee)}
          />
        </Form.Layout>
      )}

      <Form.Actions>
        <Button withLoader={isEditing} disabled={isEditing || !isValid} type="submit" data-testid="workspace-save">
          {t('buttons.save')}
        </Button>
        <Button variant="outlined" onClick={onCancel}>
          {t('buttons.cancel')}
        </Button>
      </Form.Actions>
    </Form.Container>
  );
};

export default withForm(AddWorkspaceForm);
